import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchRedesigns } from '@/services/redesigns';
import { fetchMetrics } from '@/services/metrics';
import { Icon } from '@/components/Icon';
import type { ProjectSummary } from '@/types';

const PROJECT_ICONS = [
  'auto_awesome_motion',
  'data_thresholding',
  'terminal',
  'dynamic_form',
  'widgets',
  'hub',
  'layers',
  'grid_view',
];

/** Map project status + task data to a display badge */
function getStatusStyle(
  status: string,
  pageCount: number,
  taskSummary?: { totalTasks: number; completedTasks: number },
): { label: string; bg: string; color: string } {
  const s = status.toUpperCase();
  if (s === 'FAILED' || s === 'BLOCKED') return { label: 'Failed', bg: '#fee2e2', color: '#dc2626' };
  if (s === 'PENDING') return { label: 'Starting', bg: '#f1f5f9', color: '#64748b' };
  if (s === 'CAPTURING') return { label: 'Capturing', bg: '#fef3c7', color: '#b45309' };
  if (s === 'ANALYZING') return { label: 'Analyzing', bg: '#e0e7ff', color: '#4338ca' };

  // Check task completion
  if (taskSummary && taskSummary.totalTasks > 0) {
    if (taskSummary.completedTasks >= taskSummary.totalTasks) return { label: 'Complete', bg: '#dcfce7', color: '#15803d' };
    return { label: 'In Progress', bg: '#e0e7ff', color: '#4338ca' };
  }

  if (s === 'ANALYZED') return { label: 'Analyzed', bg: '#dcfce7', color: '#15803d' };
  if (s === 'CAPTURED') {
    if (pageCount === 0) return { label: 'Empty', bg: '#f1f5f9', color: '#64748b' };
    return { label: 'Ready', bg: '#dbeafe', color: '#1e40af' };
  }
  return { label: status, bg: '#f1f5f9', color: '#64748b' };
}

export function ProjectsHub() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const { data: redesignsData } = useQuery({
    queryKey: ['redesigns'],
    queryFn: () => fetchRedesigns({}, 1, 100),
    refetchInterval: 10_000,
  });

  const { data: metricsData } = useQuery({
    queryKey: ['metrics'],
    queryFn: fetchMetrics,
    refetchInterval: 30_000,
  });

  const projects: ProjectSummary[] = useMemo(() => {
    const items: ProjectSummary[] = [];

    for (const r of redesignsData?.redesigns ?? []) {
      items.push({
        id: r._id,
        type: 'redesign',
        title: r.name || 'Untitled',
        repo: r.repo,
        status: r.status,
        pageCount: r.pages?.length ?? 0,
        taskSummary: r.taskSummary,
        thumbnailUrl:
          r.pages?.find((p) => p.variants?.length > 0)?.variants[0]?.imageUrl ||
          r.pages?.find((p) => p.originalScreenshotUrl)?.originalScreenshotUrl,
        createdAt: r.createdAt,
        updatedAt: r.updatedAt,
      });
    }

    items.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return items;
  }, [redesignsData]);

  const filtered = useMemo(() => {
    return projects.filter((p) => {
      if (showArchived) {
        if (p.status.toUpperCase() !== 'ARCHIVED') return false;
      } else {
        if (p.status.toUpperCase() === 'ARCHIVED') return false;
      }
      if (statusFilter && p.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return p.title.toLowerCase().includes(q) || p.repo.toLowerCase().includes(q);
      }
      return true;
    });
  }, [projects, search, statusFilter, showArchived]);

  const allStatuses = useMemo(() => {
    const set = new Set(projects.map((p) => p.status));
    return Array.from(set).sort();
  }, [projects]);

  const handleClick = (p: ProjectSummary) => {
    navigate(`/ui-orchestrator/projects/redesign-${p.id}`);
  };

  // Redesign statuses from backend: pending | capturing | captured | analyzing | analyzed | failed
  const totalProjects = projects.length;
  const byStatus = metricsData?.redesignsByStatus ?? {};
  const activeJobs = (byStatus['pending'] || 0) + (byStatus['capturing'] || 0) + (byStatus['analyzing'] || 0);
  const succeeded = (byStatus['captured'] || 0) + (byStatus['analyzed'] || 0);
  const failed = byStatus['failed'] || 0;
  const totalJobsDone = succeeded + failed;
  const successRate = totalJobsDone > 0
    ? Math.round((succeeded / totalJobsDone) * 1000) / 10
    : 100;
  return (
    <div>
      {/* Hero Header */}
      <section
        style={{
          marginBottom: 40,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 24,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 36,
              fontWeight: 800,
              letterSpacing: '-0.025em',
              color: 'var(--text-primary)',
              margin: 0,
              lineHeight: 1.2,
            }}
          >
            Projects Hub
          </h1>
          <p style={{ color: 'var(--text-secondary)', maxWidth: 512, margin: '4px 0 0', fontSize: 14 }}>
            Manage your distributed micro-frontends and orchestration workflows with precision.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Active / Archived toggle */}
          <div
            style={{
              display: 'flex',
              backgroundColor: 'var(--color-surface-container-low, #eff1f2)',
              padding: 4,
              borderRadius: 4,
            }}
          >
            <button
              onClick={() => setShowArchived(false)}
              style={{
                padding: '8px 16px',
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                transition: 'all 150ms ease',
                backgroundColor: !showArchived ? '#ffffff' : 'transparent',
                color: !showArchived ? 'var(--color-primary)' : '#64748b',
                boxShadow: !showArchived ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}
            >
              Active
            </button>
            <button
              onClick={() => setShowArchived(true)}
              style={{
                padding: '8px 16px',
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                transition: 'all 150ms ease',
                backgroundColor: showArchived ? '#ffffff' : 'transparent',
                color: showArchived ? 'var(--color-primary)' : '#64748b',
                boxShadow: showArchived ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}
            >
              Archived
            </button>
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: 4,
                border: 'none',
                backgroundColor: 'var(--color-surface-container-high, #e0e3e4)',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--text-primary)',
                cursor: 'pointer',
              }}
            >
              <option value="">All statuses</option>
              {allStatuses.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* Search */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ position: 'relative', maxWidth: 400 }}>
          <span
            style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#94a3b8',
              display: 'flex',
            }}
          >
            <Icon name="search" size={18} />
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search projects..."
            style={{
              width: '100%',
              backgroundColor: 'var(--color-surface-container-low, #eff1f2)',
              border: 'none',
              borderRadius: 4,
              paddingLeft: 40,
              paddingRight: 16,
              paddingTop: 10,
              paddingBottom: 10,
              fontSize: 14,
              outline: 'none',
              color: 'var(--text-primary)',
            }}
          />
        </div>
      </div>

      {/* Bento Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 24 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
          {/* Left: Project Cards */}
          <div>
            {filtered.length === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '48px 16px',
                  color: 'var(--text-muted)',
                  backgroundColor: '#fff',
                  borderRadius: 16,
                }}
              >
                <p style={{ fontSize: 15, marginBottom: 8 }}>No projects found</p>
                <p style={{ fontSize: 13 }}>Create a new project to get started.</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24 }}>
                {filtered.map((p, idx) => {
                  const statusStyle = getStatusStyle(p.status, p.pageCount, p.taskSummary);
                  const icon = PROJECT_ICONS[idx % PROJECT_ICONS.length];
                  return (
                    <div
                      key={`${p.type}-${p.id}`}
                      onClick={() => handleClick(p)}
                      style={{
                        backgroundColor: '#ffffff',
                        padding: 24,
                        borderRadius: 16,
                        cursor: 'pointer',
                        transition: 'all 200ms ease',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = '0 12px 40px rgba(74, 64, 224, 0.06)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      {/* Top row: icon + status */}
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: 16,
                        }}
                      >
                        <div
                          style={{
                            width: 48,
                            height: 48,
                            backgroundColor: '#eef2ff',
                            borderRadius: 12,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--color-primary)',
                          }}
                        >
                          <Icon name={icon} />
                        </div>
                        <span
                          style={{
                            padding: '4px 12px',
                            backgroundColor: statusStyle.bg,
                            color: statusStyle.color,
                            fontSize: 10,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            borderRadius: 9999,
                          }}
                        >
                          {statusStyle.label}
                        </span>
                      </div>

                      {/* Title + description */}
                      <h3
                        style={{
                          fontSize: 18,
                          fontWeight: 700,
                          color: 'var(--text-primary)',
                          marginBottom: 8,
                          lineHeight: 1.3,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          margin: '0 0 8px',
                        }}
                      >
                        {p.title}
                      </h3>
                      <p
                        style={{
                          color: 'var(--text-secondary)',
                          fontSize: 13,
                          marginBottom: 24,
                          lineHeight: 1.6,
                          margin: '0 0 24px',
                        }}
                      >
                        {p.repo}
                      </p>

                      {/* Bottom divider + meta */}
                      <div
                        style={{
                          marginTop: 'auto',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          paddingTop: 16,
                          borderTop: '1px solid #f1f5f9',
                        }}
                      >
                        <span
                          style={{
                            padding: '2px 8px',
                            borderRadius: 9999,
                            fontSize: 10,
                            fontWeight: 600,
                            color: '#4338ca',
                            backgroundColor: '#e0e7ff',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                        >
                          Redesign
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8' }}>
                          {new Date(p.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right: Stats Sidebar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* System Health Card */}
            <div
              style={{
                backgroundColor: 'var(--color-surface-container-low, #eff1f2)',
                padding: 32,
                borderRadius: 16,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 32,
                }}
              >
                <h4 style={{ fontWeight: 700, fontSize: 18, margin: 0 }}>System Health</h4>
                <span
                  style={{
                    width: 12,
                    height: 12,
                    backgroundColor: '#22c55e',
                    borderRadius: '50%',
                    display: 'inline-block',
                    boxShadow: '0 0 12px rgba(16, 185, 129, 0.5)',
                    animation: 'pulse 2s infinite',
                  }}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                {/* Stat: Total Projects */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      backgroundColor: '#ffffff',
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--color-primary)',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    }}
                  >
                    <Icon name="database" size={20} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p
                      style={{
                        fontSize: 10,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        fontWeight: 700,
                        color: '#94a3b8',
                        margin: 0,
                      }}
                    >
                      Total Projects
                    </p>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                      <h5 style={{ fontSize: 24, fontWeight: 900, margin: 0 }}>{totalProjects}</h5>
                    </div>
                  </div>
                </div>

                {/* Stat: Active Jobs */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      backgroundColor: '#ffffff',
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--color-primary)',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    }}
                  >
                    <Icon name="cycle" size={20} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <p
                      style={{
                        fontSize: 10,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        fontWeight: 700,
                        color: '#94a3b8',
                        margin: 0,
                      }}
                    >
                      Active Jobs
                    </p>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                      <h5 style={{ fontSize: 24, fontWeight: 900, margin: 0 }}>{activeJobs}</h5>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#4338ca' }}>
                        {activeJobs > 0 ? 'Running' : 'Idle'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Compute Efficiency Card */}
              <div
                style={{
                  marginTop: 32,
                  background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                  padding: 24,
                  borderRadius: 16,
                  color: '#fff',
                  boxShadow: '0 8px 24px rgba(5, 150, 105, 0.2)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Decorative glow */}
                <div
                  style={{
                    position: 'absolute',
                    top: -48,
                    right: -48,
                    width: 128,
                    height: 128,
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderRadius: '50%',
                    filter: 'blur(32px)',
                  }}
                />
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 16,
                    }}
                  >
                    <Icon name="bolt" size={28} />
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        opacity: 0.8,
                      }}
                    >
                      Job Success Rate
                    </span>
                  </div>
                  <h4 style={{ fontSize: 28, fontWeight: 900, margin: '0 0 4px' }}>{successRate}%</h4>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: 500, margin: '0 0 24px' }}>
                    Completed jobs success rate
                  </p>

                  {/* Mini sparkline bars */}
                  <div
                    style={{
                      height: 48,
                      width: '100%',
                      display: 'flex',
                      alignItems: 'flex-end',
                      gap: 4,
                      marginBottom: 8,
                    }}
                  >
                    {[0.5, 0.75, 0.33, 0.66, 1, 0.83, 0.75, 0.5].map((h, i) => (
                      <div
                        key={i}
                        style={{
                          flex: 1,
                          height: `${h * 100}%`,
                          backgroundColor: `rgba(255,255,255,${0.2 + h * 0.4})`,
                          borderRadius: 9999,
                        }}
                      />
                    ))}
                  </div>

                  <button
                    onClick={() => navigate('/ui-orchestrator/')}
                    style={{
                      width: '100%',
                      padding: '8px 0',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      border: 'none',
                      borderRadius: 4,
                      color: '#fff',
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      cursor: 'pointer',
                      backdropFilter: 'blur(4px)',
                    }}
                  >
                    View Details
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* FAB - Floating Action Button */}
      <div style={{ position: 'fixed', bottom: 32, right: 32, zIndex: 50 }}>
        <button
          onClick={() => navigate('/ui-orchestrator/projects/new')}
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #4a40e0 0%, #9795ff 100%)',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            cursor: 'pointer',
            boxShadow: '0 8px 24px rgba(74, 64, 224, 0.35)',
            transition: 'transform 200ms ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <Icon name="add" size={28} />
        </button>
      </div>
    </div>
  );
}
